import { CircularProgress } from "@mui/material"
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import { type ChangeEvent, type FC, useState } from "react"
import { PRODUCT_QUALITY_LAYOUT_ROUTE_PATH } from "routes/app/product/$productId/edit/_layout/quality/_layout"

import { useProductTestActions } from "../hooks/useProductTestActions"
import { useProductTestsTable } from "../hooks/useProductTestsTable"
import { ProductTestActionDialogType } from "../types"

import { NoProductTestsAlert } from "./NoProductTestsAlert"
import { ProductTestFormDialog } from "./ProductTestFormDialog"
import { ProductTestsTable } from "./ProductTestsTable"
import { QualityApprovalCancelDialog } from "./QualityApprovalCancelDialog"

import { Button } from "@/components/common/Button"
import { ConfirmationDialog } from "@/components/common/Modals"
import SearchInput from "@/components/SearchInput/SearchInput"
import { PermissionActionType } from "@/constants/permissions"
import type { ProductTest } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import OverrideTestResultsDialog from "@/screens/Products/components/Product/Quality/TestResults/components/OverrideTestResultsDialog/OverrideTestResultsDialog"
import ProductTestDocumentsDialog from "@/screens/Products/components/Product/Quality/TestResults/components/ProductTestDocumentsDialog/ProductTestDocumentsDialog"
import { useDeleteProductTest } from "@/screens/Products/components/Product/Quality/TestResults/hooks"
import { selectProduct, useProductStore } from "@/stores/useProductStore"

export const ProductTestsContainer: FC = () => {
  const [isPoLinkMode, setIsPoLinkMode] = useState(false)
  const [linkingOrderId, setLinkingOrderId] = useState<string | null>(null)
  const params = useParams({ from: PRODUCT_QUALITY_LAYOUT_ROUTE_PATH, strict: true })
  const productId = Number(params.productId)
  const { hasPermissions } = usePermissionManager()
  const { deleteProductTest } = useDeleteProductTest()
  const product = useProductStore(selectProduct)
  const {
    productTests,
    isFetching: isFetchingProductTests,
    searchQuery,
    expandedRow,
    tableContainerRef,
    refetch,
    setExpandedRow,
    handleScroll,
    handleSearchChange,
    handleViewHistory,
  } = useProductTestsTable(productId)
  const {
    openedDialog,
    associatedTestTemplates,
    productTest,
    productTestResult,
    documents,
    isDocumentsFetching,
    handleFormDialogOpen,
    handleDeleteDialogOpen,
    handleDocumentsDialogOpen,
    handleOverrideDialogOpen,
    handleApprovalRequest,
    handleApprovalCancelDialogOpen,
    handleApprovalCancelConfirm,
    handleDialogClose,
    handleDialogSubmit,
  } = useProductTestActions({ productId, onRefresh: refetch })
  const hasNoProductTests = !searchQuery && productTests.length === 0

  const handleRemoveTestLot = async (): Promise<void> => {
    if (!productTest) return
    const isDeleted = await deleteProductTest(productTest)

    if (!isDeleted) return

    handleDialogClose()
    refetch()
  }

  const handleLinkProductTestToPo = (productTest: ProductTest | null, orderId: string) => {
    const editedProductTest = productTests.find((pt) => pt.productTestId === productTest?.productTestId)
    handleFormDialogOpen(editedProductTest)
    setIsPoLinkMode(true)
    setLinkingOrderId(orderId)
  }

  const resetPoLinking = () => {
    setIsPoLinkMode(false)
    setLinkingOrderId(null)
  }

  const handleFormDialogClose = () => {
    handleDialogClose()
    resetPoLinking()
  }

  const handleFormDialogSubmit = () => {
    handleDialogSubmit()
    resetPoLinking()
  }

  if (hasNoProductTests && isFetchingProductTests) {
    return (
      <Box className='flex size-full items-center justify-center'>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      {hasNoProductTests ? (
        <NoProductTestsAlert onAdd={() => handleFormDialogOpen()} />
      ) : (
        <Paper variant='outlined' elevation={0} className='flex h-full flex-col gap-y-6 border-gray-400 p-6'>
          <Box className='flex shrink-0 items-center gap-x-4'>
            <SearchInput
              placeholder='Search by Lot Number'
              defaultValue={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
              className='mr-auto w-full max-w-[524px]'
            />
            {hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE]) && (
              <Button startIcon={<AddOutlined />} onClick={() => handleFormDialogOpen()}>
                Test Data
              </Button>
            )}
          </Box>
          <Box className='flex flex-1 flex-col overflow-y-auto'>
            <ProductTestsTable
              data={productTests}
              isLoading={isFetchingProductTests}
              expandedRow={expandedRow}
              tableContainerRef={tableContainerRef}
              setExpandedRow={setExpandedRow}
              onEdit={handleFormDialogOpen}
              onDelete={handleDeleteDialogOpen}
              onViewDocuments={handleDocumentsDialogOpen}
              onViewHistory={handleViewHistory}
              onOverride={handleOverrideDialogOpen}
              onApprovalRequest={handleApprovalRequest}
              onApprovalCancel={handleApprovalCancelDialogOpen}
              onScroll={handleScroll}
            />
          </Box>
        </Paper>
      )}

      {openedDialog === ProductTestActionDialogType.Form && product && !isDocumentsFetching && (
        <ProductTestFormDialog
          product={product}
          productTest={productTest ? { ...productTest, documents } : null}
          associatedTestTemplates={associatedTestTemplates}
          linkingOrderId={linkingOrderId}
          isPoLinkMode={isPoLinkMode}
          onPoLink={handleLinkProductTestToPo}
          onClose={handleFormDialogClose}
          onSubmit={handleFormDialogSubmit}
        />
      )}
      {openedDialog === ProductTestActionDialogType.DeleteConfirmation && (
        <ConfirmationDialog
          title='Remove Lot'
          content='Are you sure you want to remove this Lot?'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          onClose={handleDialogClose}
          onConfirm={handleRemoveTestLot}
        />
      )}
      {openedDialog === ProductTestActionDialogType.Documents && (
        <ProductTestDocumentsDialog
          onClose={handleDialogSubmit}
          productTest={productTest}
          companyName={String(product?.company?.name)}
        />
      )}
      {openedDialog === ProductTestActionDialogType.Override && productTest && productTestResult && (
        <OverrideTestResultsDialog
          onClose={handleDialogClose}
          productTestResult={productTestResult}
          productTest={{ ...productTest, documents }}
          onSubmit={handleDialogSubmit}
        />
      )}
      {openedDialog === ProductTestActionDialogType.ApprovalCancelConfirmation && (
        <QualityApprovalCancelDialog onClose={handleDialogClose} onConfirm={handleApprovalCancelConfirm} />
      )}
    </>
  )
}
